import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ApiResponse,
  PageResponse,
  SearchCriteria,
  buildSearchParams,
  emptyPage,
} from '../core/types/api.types';
import { UserType } from './auth.service';
import { Page } from 'ngx-pagination';

export interface AppUser {
  reference?: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  countryCode?: string;
  typeUser: UserType;
  appUserStatus?:
    | 'INITIAL'
    | 'PENDING'
    | 'ACTIVE'
    | 'INACTIVE'
    | 'SUSPENDED'
    | 'BLOCKED';
  profil?: string;
  locale?: string;
  timezone?: string;
  avatarUrl?: string;
  createdAt?: string;
  lastLoginAt?: string;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  twoFactorEnabled?: boolean;
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  countryCode?: string;
  locale?: string;
  timezone?: string;
  avatarUrl?: string;
}

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/users`;

  /** Profil utilisateur courant */
  me(): Observable<AppUser> {
    return this.http
      .get<ApiResponse<AppUser>>(`${this.base}/me`)
      .pipe(map((r) => r.data));
  }

  updateProfile(data: UpdateProfileRequest): Observable<AppUser> {
    return this.http
      .put<ApiResponse<AppUser>>(`${this.base}/me`, data)
      .pipe(map((r) => r.data));
  }

  changePassword(data: ChangePasswordRequest): Observable<void> {
    return this.http
      .post<ApiResponse<void>>(`${this.base}/me/password`, data)
      .pipe(map(() => undefined));
  }

  uploadAvatar(file: File): Observable<{ avatarUrl: string }> {
    const fd = new FormData();
    fd.append('file', file);
    return this.http
      .post<ApiResponse<{ avatarUrl: string }>>(`${this.base}/me/avatar`, fd)
      .pipe(map((r) => r.data));
  }

  toggleTwoFactor(enabled: boolean): Observable<void> {
    return this.http
      .post<ApiResponse<void>>(`${this.base}/me/2fa`, { enabled })
      .pipe(map(() => undefined));
  }

  // ─── ADMIN ────────────────────────────────────────────────────────────────

  search(c: SearchCriteria): Observable<PageResponse<AppUser>> {
    const params = new HttpParams({ fromObject: buildSearchParams(c) });
    return this.http.get<PageResponse<AppUser>>(this.base + '/search', {
      params,
    });
    // return this.http
    //   .get<
    //     ApiResponse<PageResponse<AppUser>>
    //   >(`${this.base}/search`, { params })
    //   .pipe(map((r) => r?.data || emptyPage<AppUser>()));
  }

  getByReference(ref: string): Observable<AppUser> {
    return this.http
      .get<ApiResponse<AppUser>>(`${this.base}/${ref}`)
      .pipe(map((r) => r.data));
  }

  activate(ref: string): Observable<AppUser> {
    return this.http
      .post<ApiResponse<AppUser>>(`${this.base}/${ref}/activate`, {})
      .pipe(map((r) => r.data));
  }

  suspend(ref: string, raison?: string): Observable<AppUser> {
    return this.http
      .post<ApiResponse<AppUser>>(`${this.base}/${ref}/suspend`, { raison })
      .pipe(map((r) => r.data));
  }

  block(ref: string, raison?: string): Observable<AppUser> {
    return this.http
      .post<ApiResponse<AppUser>>(`${this.base}/${ref}/block`, { raison })
      .pipe(map((r) => r.data));
  }

  resetPassword(ref: string): Observable<void> {
    return this.http
      .post<ApiResponse<void>>(`${this.base}/${ref}/reset-password`, {})
      .pipe(map(() => undefined));
  }

  changeRole(ref: string, typeUser: UserType): Observable<AppUser> {
    return this.http
      .post<ApiResponse<AppUser>>(`${this.base}/${ref}/role`, { typeUser })
      .pipe(map((r) => r.data));
  }
}

export const USER_STATUS_LABELS: Record<string, string> = {
  INITIAL: 'Initial',
  PENDING: 'En attente',
  ACTIVE: 'Actif',
  INACTIVE: 'Inactif',
  SUSPENDED: 'Suspendu',
  BLOCKED: 'Bloqué',
};

export const USER_STATUS_COLORS: Record<string, string> = {
  INITIAL: 'bg-muted/30 text-muted',
  PENDING: 'bg-warning/10 text-warning',
  ACTIVE: 'bg-success/10 text-success',
  INACTIVE: 'bg-muted/30 text-muted',
  SUSPENDED: 'bg-warning/10 text-warning',
  BLOCKED: 'bg-error/10 text-error',
};

export const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Administrateur',
  BUSINESS: 'Atelier',
  CUSTOMER: 'Client',
  PARTNER: 'Partenaire',
  APP: 'App système',
};
